"use client";

import type React from "react";

import { useState } from "react";
import styles from "./CreateSegments.module.css";
import { FiX, FiPlus, FiAlertCircle } from "react-icons/fi";
import { useCreateSegment } from "API/Queries/useCreateSegment";
import type { CreateSegmentsPayload } from "Interfaces/FeatureFlip";
import { RotatingLines } from "react-loader-spinner";
import AddUsers from "Components/FeatureFlip/Common/AddUser/AddUsers";
import ContextSelector from "Components/FeatureFlip/Common/ContextSelector/ContextSelector";
import { useFeatureFlipStore } from "Store/featureFlipStore";
import { useGetAllSegments } from "API/Queries/useGetAllSegments";
import RequiredStar from "Components/Common/RequiredStar";
import { emptyStringsToNull } from "Utils/utils";

interface ICreateSegmentsProps {
  setShowAddForm: (showAddForm: boolean) => void;
}

const initialFormData: CreateSegmentsPayload = {
  segmentName: "",
  segmentDescription: "",
  contextId: "",
  contextName: "",
  contextDescription: "",
  contextValues: [],
};

const CreateSegments = (props: ICreateSegmentsProps) => {
  const { setShowAddForm } = props;

  const [formData, setFormData] =
    useState<CreateSegmentsPayload>(initialFormData);
  const [contextNameValidationError, setContextNameValidationError] =
    useState("");
  const [error, setError] = useState<string>();

  const { mutateAsync: createSegment, isLoading } = useCreateSegment();
  const contexts = useFeatureFlipStore((s) => s.contexts);
  const setSegments = useFeatureFlipStore((s) => s.setSegments);
  const selectedProjectId = useFeatureFlipStore((s) => s.selectedProjectId);
  const setHasMoreDataSegments = useFeatureFlipStore(
    (s) => s.setHasMoreDataSegments
  );
  const setPageSegments = useFeatureFlipStore((s) => s.setPageSegments);

  const { refetch: getAllSegments } = useGetAllSegments(
    {
      query: `projectId==${selectedProjectId}`,
      pageNumber: 1,
      pageSize: 10,
    },
    {
      onSuccess: (segments) => {
        window.scroll(0, 0);
        setSegments(segments);
        setPageSegments(1);
        setShowAddForm(false);
        resetForm();

        const hasMaxItems = segments.length === 10;
        if (hasMaxItems) {
          setHasMoreDataSegments(true);
        }
      },
    }
  );

  // Extract complex logic
  const isSegmentNameEmpty = !formData.segmentName.trim();
  const hasContextValues = !!formData.contextValues?.length;
  const hasNoContextSelected = !formData.contextName && !formData.contextId;
  const needsContextForUsers = hasContextValues && hasNoContextSelected;
  const isFormInvalid = isSegmentNameEmpty;

  const resetForm = () => {
    setFormData(initialFormData);
    setContextNameValidationError("");
    setError(undefined);
  };

  // Extract event handlers
  const handleSegmentNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, segmentName: e.target.value });
  };

  const handleSegmentDescriptionChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, segmentDescription: e.target.value });
  };

  const handleCloseModal = () => {
    setShowAddForm(false);
    resetForm();
  };

  const handleCreateSegment = async () => {
    const isDuplicateContextName =
      formData.contextName &&
      contexts.some(
        (c) => c.name?.toLowerCase() === formData.contextName?.toLowerCase()
      );

    if (isDuplicateContextName) {
      setContextNameValidationError("A context with this name already exists");
      return;
    }

    if (needsContextForUsers) {
      setError("context is required to add users");
      setTimeout(() => {
        setError(undefined);
      }, 1000);
      return;
    }

    setError(undefined);
    const payload = emptyStringsToNull(formData) as CreateSegmentsPayload;

    await createSegment(
      { ...payload, projectId: selectedProjectId },
      {
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof Error ? error.message : "Failed to create segment";
          setError(errorMessage);
        },
      }
    );

    await getAllSegments();
  };

  const getCreateButtonContent = () => {
    if (isLoading) {
      return (
        <>
          <RotatingLines
            strokeColor="white"
            strokeWidth="5"
            animationDuration="0.75"
            width="20"
            visible={true}
          />
          Creating...
        </>
      );
    }

    return (
      <>
        <FiPlus /> Create Segment
      </>
    );
  };

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2>Create New Segment</h2>
          {!isLoading && (
            <button className={styles.closeButton} onClick={handleCloseModal}>
              <FiX />
            </button>
          )}
        </div>

        <div className={styles.modalBody}>
          <div className={styles.formGroup}>
            <label>
              Name <RequiredStar />
            </label>
            <input
              type="text"
              value={formData.segmentName}
              onChange={handleSegmentNameChange}
              placeholder="e.g., Premium Users"
              className={styles.input}
              required
              disabled={isLoading}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Description</label>
            <textarea
              value={formData.segmentDescription}
              onChange={handleSegmentDescriptionChange}
              placeholder="Describe who belongs in this segment"
              className={styles.textarea}
              required
              disabled={isLoading}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Context</label>
            <ContextSelector
              contexts={contexts}
              duplicateContextNameErr={contextNameValidationError}
              setDuplicateNameErr={setContextNameValidationError}
              formData={formData}
              setFormData={setFormData}
            />
          </div>

          <div className={styles.formGroup}>
            <label>Add Users (optional)</label>
            <AddUsers formData={formData} setFormData={setFormData} />
          </div>
        </div>

        {error && (
          <p className={styles.error}>
            <FiAlertCircle />
            {error}
          </p>
        )}

        <div className={styles.modalFooter}>
          <button
            onClick={handleCreateSegment}
            className={styles.saveButton}
            disabled={isFormInvalid}
          >
            {getCreateButtonContent()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSegments;
